import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Search, Mail, BookOpen, CreditCard, Settings, MessageCircle } from 'lucide-react';
import Footer from '@/components/Footer';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = [
    {
      title: 'Getting Started',
      icon: BookOpen,
      faqs: [
        {
          question: 'How do I create my first course?',
          answer: 'Navigate to the Courses page and click the "Create Course" button. Enter a historical topic (e.g., "Ancient Rome", "World War II"), select your complexity level (Novice, Proficient, or Scholar), and click "Generate Course". Our AI will create a personalized curriculum for you in about 1-2 minutes.'
        },
        {
          question: 'What are the different complexity levels?',
          answer: 'Novice (2-3 hours): Perfect for newcomers, covers 6 engaging lessons with introduction to key concepts. Proficient (5-8 hours): Deepens your knowledge with 7 comprehensive lessons and detailed analysis. Scholar (~10 hours): Master-level content with 8 in-depth lessons, research-level material, and comprehensive examinations.'
        },
        {
          question: 'How does the History Chat Room work?',
          answer: 'Professor Clio is your AI history tutor available 24/7. Simply navigate to the Chat page and ask any history-related question. You can discuss events, figures, causes and effects, or get help understanding complex topics. The AI provides detailed, educational responses based on historical scholarship.'
        },
        {
          question: 'What is the Debate Room?',
          answer: 'The Debate Room helps you practice historical argumentation. Choose a debate topic, take a position, and engage in a structured debate with our AI opponent. You\'ll receive detailed feedback on your logic, evidence, rhetoric, and rebuttal skills.'
        }
      ]
    },
    {
      title: 'Courses & Learning',
      icon: BookOpen,
      faqs: [
        {
          question: 'How do I navigate through lessons?',
          answer: 'Each lesson has multiple sections you can expand by clicking on them. Read the content, complete section quizzes, and finish with the final lesson quiz. Use the "Next Lesson" and "Previous Lesson" buttons to navigate. Your progress is automatically saved.'
        },
        {
          question: 'Can I favorite courses?',
          answer: 'Yes! Click the star icon on any course card to add it to your favorites. Access your favorite courses by clicking the "Favorites" tab on the Courses page. This helps you quickly find courses you want to revisit.'
        },
        {
          question: 'What are section quizzes vs final quizzes?',
          answer: 'Section quizzes appear after each section content to test your understanding of that specific topic. Final quizzes cover the entire lesson and must be passed to mark the lesson as complete. You can retake any quiz as many times as needed.'
        },
        {
          question: 'How do I track my progress?',
          answer: 'Visit the Progress page to see all your course completion percentages, quiz scores, debate performances, and overall learning metrics. Each course card also shows your progress percentage.'
        },
        {
          question: 'Can I delete a course I created?',
          answer: 'Yes, click the trash icon on any course card. Note that this will permanently delete the course and all associated progress data.'
        }
      ]
    },
    {
      title: 'Subscription & Billing',
      icon: CreditCard,
      faqs: [
        {
          question: 'How does the free trial work?',
          answer: 'New users get a 7-day free trial with full access to all features. You can cancel anytime during the trial period without being charged. If you don\'t cancel, your subscription will automatically begin at the end of the trial.'
        },
        {
          question: 'What subscription plans are available?',
          answer: 'Monthly Plan: $9.99/month with all features included. Yearly Plan: $89.99/year (saves you $30 compared to monthly billing). Both plans include unlimited course generation, AI chat, debates, and all complexity levels.'
        },
        {
          question: 'How do I cancel my subscription?',
          answer: 'Go to Profile → Subscription tab → Click "Cancel Subscription". Your access will continue until the end of your current billing period. You can resubscribe at any time.'
        },
        {
          question: 'Can I change from monthly to yearly billing?',
          answer: 'Yes! Cancel your current subscription and then resubscribe with the yearly plan. You\'ll save $30 per year with annual billing.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'We handle refund requests on a case-by-case basis. Please contact us at help@mylyceum.org within 14 days of your purchase to request a refund.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, debit cards, and digital payment methods through our secure payment processor, Stripe.'
        }
      ]
    },
    {
      title: 'Account & Settings',
      icon: Settings,
      faqs: [
        {
          question: 'How do I update my profile information?',
          answer: 'Navigate to Profile → Profile tab. Here you can update your name, email, profile picture, and password. Changes are saved immediately.'
        },
        {
          question: 'Can I change my email address?',
          answer: 'No, your email address cannot be changed as it is linked to your account identity and authentication. If you need to use a different email, you would need to create a new account. Contact support at help@mylyceum.org if you have special circumstances.'
        },
        {
          question: 'I forgot my password. What do I do?',
          answer: 'On the login page, click "Forgot Password?" Enter your email address and new password to reset it. If you\'re having trouble, contact us at help@mylyceum.org'
        },
        {
          question: 'How do I delete my account?',
          answer: 'To delete your account and all associated data, please contact us at help@mylyceum.org. Account deletion is permanent and cannot be undone.'
        },
        {
          question: 'Can I turn off email notifications?',
          answer: 'Yes, go to Profile → Settings tab and toggle the "Email Notifications" switch to control marketing and update emails.'
        }
      ]
    },
    {
      title: 'Technical Support',
      icon: MessageCircle,
      faqs: [
        {
          question: 'The app is loading slowly. What should I do?',
          answer: 'Try refreshing the page, clearing your browser cache, or using a different browser. Course generation can take 1-2 minutes for complex topics - this is normal. If issues persist, contact support.'
        },
        {
          question: 'My course generation failed. Why?',
          answer: 'Course generation occasionally times out for very complex topics. Try simplifying your topic or choosing a lower complexity level. If the problem persists, contact us and we\'ll investigate.'
        },
        {
          question: 'I\'m not receiving email notifications.',
          answer: 'Check your spam folder first. Make sure email notifications are enabled in Profile → Settings. If still not receiving emails, contact help@mylyceum.org'
        },
        {
          question: 'Can I use Lyceum on mobile devices?',
          answer: 'Yes! Lyceum is fully responsive and works on smartphones and tablets. We recommend using the latest version of Chrome, Safari, or Firefox for the best experience.'
        },
        {
          question: 'What browsers are supported?',
          answer: 'We support the latest versions of Chrome, Firefox, Safari, and Edge. For the best experience, please keep your browser up to date.'
        }
      ]
    }
  ];

  // Filter FAQs based on search term
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  return (
    <div className="min-h-screen theme-bg">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-amber-700 hover:text-amber-900 hover:bg-amber-100 btn-glow rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-amber-900 mb-4">Help Center & FAQ</h1>
          <p className="text-xl text-amber-700 mb-8">
            Find answers to common questions about using Lyceum
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-amber-600 w-5 h-5" />
            <Input
              placeholder="Search for help..."
              className="pl-12 py-6 text-lg border-amber-300 focus:border-amber-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredCategories.length === 0 ? (
            <Card className="card-glow p-12 text-center">
              <p className="text-gray-600">No results found for "{searchTerm}". Try different keywords or contact support.</p>
            </Card>
          ) : (
            filteredCategories.map((category, idx) => (
              <Card key={idx} className="p-6 bg-white card-glow">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <category.icon className="w-6 h-6 text-amber-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-amber-900">{category.title}</h2>
                </div>

                <Accordion type="single" collapsible className="space-y-2">
                  {category.faqs.map((faq, faqIdx) => (
                    <AccordionItem
                      key={faqIdx}
                      value={`item-${idx}-${faqIdx}`}
                      className="border border-amber-200 rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:bg-amber-50 text-left">
                        <span className="font-semibold text-amber-900">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-amber-50/30">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            ))
          )}
        </div>

        {/* Contact Support Section */}
        <Card className="card-glow mt-12 p-8 bg-gradient-to-r from-amber-100 to-orange-100 border-amber-300">
          <div className="text-center">
            <Mail className="w-12 h-12 text-amber-700 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-amber-900 mb-3">Still Need Help?</h2>
            <p className="text-amber-800 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <a href="mailto:help@mylyceum.org">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                <Mail className="w-5 h-5 mr-2" />
                Contact Support
              </Button>
            </a>
            <p className="text-sm text-amber-700 mt-4">
              Email: help@mylyceum.org
            </p>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default HelpCenter;
